import React, { useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import routes from "routes";
import { RoutesType } from "types";
import { FiMenu, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const PublicLayout = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((prop, key) => {
      if (prop.layout === "/") {
        return (
          <Route path={`${prop.path}`} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  const navLinkClasses = (path: string) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      location.pathname === `/${path}`
        ? "bg-nature-green text-white"
        : "text-gray-700 hover:bg-nature-green-light hover:text-white"
    }`;

  const mobileNavLinkClasses = (path: string) =>
    `block px-4 py-3 rounded-md text-base font-medium transition-colors ${
      location.pathname === `/${path}`
        ? "bg-nature-green text-white"
        : "text-gray-700 hover:bg-nature-green-light hover:text-white"
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-complementary-light">
      <nav className="fixed z-50 w-full bg-white/90 shadow-sm backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex-shrink-0">
              <Link to="/home" className="flex items-center">
                <img
                  src={require("assets/img/logo/logo.png")}
                  alt="Nunya Logo"
                  className="h-20 w-auto max-h-20 md:h-28 md:max-h-28 lg:h-36 lg:max-h-36 transition-all duration-200"
                  style={{ maxHeight: '6rem', width: 'auto' }}
                  sizes="(max-width: 640px) 180px, (max-width: 1024px) 260px, 340px"
                />
              </Link>
            </div>
            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link to="/home" className={navLinkClasses("home")}> 
                  Home
                </Link>
                <Link to="/about" className={navLinkClasses("about")}> 
                  About
                </Link>
                <Link
                  to="/projects"
                  className={navLinkClasses("projects")}
                >
                  Projects
                </Link>
                <Link
                  to="/businesses"
                  className={navLinkClasses("businesses")}
                >
                  Businesses
                </Link>
                <Link
                  to="/contact"
                  className={navLinkClasses("contact")}
                >
                  Contact
                </Link>
                <Link
                  to="/auth/sign-in"
                  className="rounded-md bg-nature-green px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-nature-green-dark"
                >
                  Sign In
                </Link>
              </div>
            </div>
            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="rounded-md p-2 text-nature-green-dark hover:bg-gray-100 focus:outline-none"
              >
                {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden"
            >
              <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
                <Link
                  to="/home"
                  className={mobileNavLinkClasses("home")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className={mobileNavLinkClasses("about")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/projects"
                  className={mobileNavLinkClasses("projects")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Projects
                </Link>
                <Link
                  to="/businesses"
                  className={mobileNavLinkClasses("businesses")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Businesses
                </Link>
                <Link
                  to="/contact"
                  className={mobileNavLinkClasses("contact")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/auth/sign-in"
                  className="mt-2 block w-full rounded-md bg-nature-green px-4 py-3 text-left text-base font-medium text-white transition-colors hover:bg-nature-green-dark"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="flex-grow pt-16">
        <Routes>{getRoutes(routes)}</Routes>
      </main>

      <footer className="bg-nature-green-dark text-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center md:items-start">
              <Link to="/home" className="mb-4 flex items-center justify-center md:justify-start">
                <img
                  src={require("assets/img/logo/logo.png")}
                  alt="Nunya Logo"
                  className="h-24 w-auto max-h-24 md:h-32 md:max-h-32 lg:h-40 lg:max-h-40 transition-all duration-200"
                  style={{ maxHeight: '10rem', width: 'auto' }}
                  sizes="(max-width: 640px) 220px, (max-width: 1024px) 320px, 400px"
                />
              </Link>
              <p className="text-gray-300 text-center md:text-left">
                Fostering connection and collaboration.
              </p>
            </div>
            <div>
              <h3 className="mb-4 text-xl font-bold">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-gray-300 hover:text-white"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/projects"
                    className="text-gray-300 hover:text-white"
                  >
                    Projects
                  </Link>
                </li>
                <li>
                  <Link
                    to="/businesses"
                    className="text-gray-300 hover:text-white"
                  >
                    Businesses
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-300 hover:text-white"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/age-grades"
                    className="text-gray-300 hover:text-white"
                  >
                    Age Grades
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-4 text-xl font-bold">Follow Us</h3>
              {/* Social media icons can be added here */}
            </div>
          </div>
          <div className="mt-8 border-t border-nature-green pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Nunya. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
